import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../reduxStore/redux-hooks';
import DeleteModal from '../../../../sharedComponents/DeleteModal';
import Api, { ApiError } from '../../../../../data/api/Api';
import { showUserActionContentOnChat } from '../../../../../reduxStore/slices/NotifyUserActionContentSlice';
import { SectionDivider } from '../../../../staticComponents/StaticHtmlGenerator';
import { AccountCircle, BoyOutlined } from '@mui/icons-material';
import User from '../../../../../helpers/User';
import ImageButton from '../../../../../components/sharedComponents/ImageButton';
import * as assets from '../../../../../assets';
import { MyTeamsData } from '../myTeams/MyTeams';
import ShareTeam from '../shareTeam/ShareTeam';
import {
  confirmTeamUpdate,
  refreshTeamsList,
  resetActiveTeam,
  setIsTeamOwner,
  setIsTeamView,
  updateTeam,
} from '../../../../../reduxStore/slices/TeamsSlice';
import { editAgentOrTeamListStyles } from '../../agents/editAgent/EditAgent';
import { lengthOfVisibleEditAgentOrTeamName } from '../../agents/myAgents/MyAgents';
import { closeBasicPopoverManually, setOpenCreateAgentOrTeamPage } from '../../../../../reduxStore/slices/CommonSlice';
import { UserDetails } from '../../developerKey/DeveloperKey';
import { showSnackbarErrorMessage } from '../../../../../components/staticComponents/staticUtiles';
import { LogButtonEventToGA } from '../../../../../components/sharedComponents/googleAnalytics/GoogleAnalytics';
import { enqueueSnackbar } from 'notistack';
import UseAssistantSharedUsers from '../../../../sharedComponents/customHooks/useAssistantSharedUsers/UseAssistantSharedUsers';
import { PopoverContentProps } from '../../../../sharedComponents/BasicPopover';

interface EditTeamProps extends PopoverContentProps {
  team: MyTeamsData;
  isActiveTeam: boolean;
}

const EditTeam: React.FC<EditTeamProps> = ({ team, isActiveTeam, popoverActionsRef }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [showShareTeamDialog, setShowShareTeamDialog] = useState<boolean>(false);

  const [selectedTeamOwners, setSelectedTeamOwners] = useState<UserDetails[]>([]);
  const [teamSharedUserToBeDeleted, setTeamSharedUserToBeDeleted] = useState<UserDetails>();
  const user = User.empty;

  const {
    sharedUsers: selectedTeamSharedUsers,
    isLoading: isLoadingMoreSharedUsers,
    hasMoreSharedUsers,
    loadInitialSharedUsersBatch: loadInitialSharedUsers,
    loadNextSharedUsersBatch: handleLoadMoreUsers,
  } = UseAssistantSharedUsers(team.acl?.users || []);

  useEffect(() => {
    fetchUserData();
    getGraphData();
  }, []);

  useEffect(() => {
    updatePopoverPosition();
  }, [selectedTeamOwners, isLoadingMoreSharedUsers, popoverActionsRef]);

  // Fetch data and update user on component mount
  const fetchUserData = async () => {
    const fetchUser = await User.getInstance();
    Object.assign(user, fetchUser);
  };

  const getGraphData = async () => {
    try {
      // Initialize shared users loading
      await loadInitialSharedUsers();

      const teamOwnersDetails = (await Api.getCompleteUserDetailsByID(team.owners)) as UserDetails[];
      setSelectedTeamOwners(teamOwnersDetails);
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
      console.log(error);
    }
  };

  const updatePopoverPosition = () => {
    if (!isLoadingMoreSharedUsers && popoverActionsRef?.current) {
      requestAnimationFrame(() => {
        popoverActionsRef.current?.updatePosition();
      });
    }
  };

  // Function to unload the active Team
  const unloadActiveTeam = () => {
    // Show a user action content on the chat
    dispatch(showUserActionContentOnChat(t('rightPanel.team.deactivateTeam', { activeTeamName: team.name })));

    // Dispatch an action to set the active team to an empty object
    dispatch(resetActiveTeam());
  };

  const shareTeam = () => {
    LogButtonEventToGA(t('rightPanel.team.editTeam.manageShare'));
    setShowShareTeamDialog(true);
  };

  const editTeam = () => {
    dispatch(setOpenCreateAgentOrTeamPage(true));
    dispatch(confirmTeamUpdate(true));
    dispatch(updateTeam(team));
  };

  const viewTeamDetails = () => {
    editTeam();
    dispatch(setIsTeamView(true));
    selectedTeamOwners[0].displayName === user.fullName
      ? dispatch(setIsTeamOwner(true))
      : dispatch(setIsTeamOwner(false));
  };

  // List of options for editing the team
  const editTeamOptionsList = [
    {
      label: t('viewModal.view'),
      sectionDivider: false,
      onClick: viewTeamDetails,
      ariaLabel: `${t('viewModal.view')}`,
      role: 'button',
      tabIndex: 0,
    },

    {
      label: t('rightPanel.team.editTeam.edit'),
      sectionDivider: false,
      onClick: editTeam,
      ariaLabel: `${t('rightPanel.team.editTeam.edit')}`,
      role: 'button',
      tabIndex: 0,
    }, // Option to customize the team
    {
      label: isActiveTeam ? t('rightPanel.team.editTeam.unloadTeam') : t('rightPanel.favourite'), // Option to unload or favorite the team
      sectionDivider: true,
      onClick: isActiveTeam ? unloadActiveTeam : () => {},
      ariaLabel: isActiveTeam ? `${t('rightPanel.team.editTeam.unloadTeam')}` : `${t('rightPanel.favourite')}`,
      role: 'button',
      tabIndex: 0,
    },
    {
      label: t('rightPanel.team.editTeam.manageShare'),
      sectionDivider: true,
      onClick: shareTeam,
      ariaLabel: `${t('rightPanel.team.editTeam.manageShare')}`,
      role: 'button',
      tabIndex: 0,
    }, // Option to manage the sharing of the team
  ];

  // Function to confirm the deletion of the team or team shared user.
  const confirmDelete = async () => {
    teamSharedUserToBeDeleted ? await deleteTeamSharedUser(teamSharedUserToBeDeleted) : await deleteTeam();
    setIsDeleteModalOpen(false);
    dispatch(closeBasicPopoverManually(true));
  };

  const deleteTeamSharedUser = async (teamSharedUserToBeDeleted: UserDetails) => {
    try {
      await Api.removeTeamSharedUser(team._id, [teamSharedUserToBeDeleted.id]);
      enqueueSnackbar(t('quickView.removedSharedUser', { userName: teamSharedUserToBeDeleted.displayName }));
      dispatch(refreshTeamsList(true));
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
      console.error('Error removing team shared user:', error);
    }
  };

  const deleteTeam = async () => {
    try {
      const deleteTeamResponse = await Api.deleteTeam(team._id);

      // If the team was deleted successfully
      if (deleteTeamResponse) {
        dispatch(showUserActionContentOnChat(t('rightPanel.team.deleteTeam', { activeTeamName: team.name })));

        if (isActiveTeam) {
          dispatch(resetActiveTeam());
        }
        dispatch(refreshTeamsList(true));
      }
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
      console.error('Error deleting team:', error);
    }
  };

  // Function to open the delete team modal
  const confirmDeleteTeam = () => {
    LogButtonEventToGA(t('rightPanel.team.editTeam.deleteTeam'));
    setIsDeleteModalOpen(true);
  };

  // Function to open the delete team shared user modal
  const confirmDeleteTeamSharedUser = (sharedUser: UserDetails) => {
    setTeamSharedUserToBeDeleted(sharedUser);
    setIsDeleteModalOpen(true);
  };

  // Function to close the delete team modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const loadTeamOwners = () => {
    return selectedTeamOwners.length > 0 ? (
      selectedTeamOwners.map((teamOwner, index) => (
        <ListItem sx={{ padding: 0, justifyContent: 'space-between' }} key={index}>
          <ListItemText sx={{ marginRight: '50px' }}>{teamOwner.displayName}</ListItemText>
          <ListItemText sx={{ textAlign: 'right' }}>
            <span
              style={{
                fontSize: '12px',
                background: 'var(--global-color)',
                color: '#fff',
                padding: '2px 5px 3px',
                borderRadius: '4px',
              }}
            >
              {t('rightPanel.dataset.ownerBadge')}
            </span>
          </ListItemText>
        </ListItem>
      ))
    ) : (
      <Typography>{t('rightPanel.dataset.ownersNotFoudMessage')}</Typography>
    );
  };

  const loadTeamSharedUsers = () => {
    return (
      <>
        <SectionDivider sx={{ marginLeft: '0', marginRight: '0', marginBottom: '10px' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex' }}>
            <SvgIcon sx={{ marginRight: '10px' }} component={AccountCircle} />
            <Typography>{t('rightPanel.dataset.sharedHeading')}</Typography>
          </Box>
          <Typography sx={{ fontWeight: 600 }}>{team.acl?.users?.length}</Typography>
        </Box>
        <List sx={{ marginLeft: '25px' }}>
          {selectedTeamSharedUsers.length > 0 ? (
            <>
              {selectedTeamSharedUsers.map((sharedUser, index) => (
                <ListItem sx={{ padding: 0, justifyContent: 'space-between' }} key={index}>
                  <ListItemText>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', width: '200px', wordBreak: 'break-all' }}>
                        <SvgIcon sx={{ marginRight: '5px' }} component={BoyOutlined} />
                        <Typography sx={{ flex: 'none', fontWeight: '600', maxWidth: '170px' }}>
                          {sharedUser.displayName}
                        </Typography>
                      </Box>
                      {selectedTeamOwners.find((owner) => owner.displayName === user.fullName) && (
                        <ListItemText sx={{ flex: 'none' }}>
                          <ImageButton
                            srcFile={assets.removeUser}
                            handleClick={() => confirmDeleteTeamSharedUser(sharedUser)}
                          />
                        </ListItemText>
                      )}
                    </Box>
                  </ListItemText>
                </ListItem>
              ))}

              {hasMoreSharedUsers && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Button
                    onClick={handleLoadMoreUsers}
                    disabled={isLoadingMoreSharedUsers}
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    {isLoadingMoreSharedUsers ? <CircularProgress size={16} /> : null}
                    {t('rightPanel.loadMore')}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography>{t('rightPanel.dataset.shareNotFoudMessage')}</Typography>
          )}
        </List>
      </>
    );
  };

  return (
    <>
      <Box sx={{ padding: '14px', width: '225px', borderRadius: '6px' }}>
        {selectedTeamOwners.length === 0 ? (
          <CircularProgress size={25} />
        ) : (
          <>
            <Tooltip title={team.name.length > lengthOfVisibleEditAgentOrTeamName ? team.name : null} arrow>
              <Typography
                fontWeight={'700'}
                fontSize={'18px'}
                color={'#000'}
                overflow={'hidden'}
                textOverflow={'ellipsis'}
              >
                {team.name}
              </Typography>
            </Tooltip>
            <List sx={{ padding: 0 }}>
              {editTeamOptionsList.map((item, index) => (
                <Box key={index}>
                  {item.label === t('rightPanel.agent.editAgent.manageShare') && (
                    <>
                      {loadTeamOwners()}
                      {loadTeamSharedUsers()}
                    </>
                  )}
                  {item.label !== t('rightPanel.favourite') &&
                    ((item.label !== t('rightPanel.agent.editAgent.edit') &&
                      item.label !== t('rightPanel.agent.editAgent.manageShare')) ||
                      selectedTeamOwners.find((owner) => owner.displayName === user.fullName)) && (
                      <ListItem key={index} sx={editAgentOrTeamListStyles}>
                        <ListItemText onClick={item.onClick} role="button" tabIndex={0}>
                          {item.label}
                        </ListItemText>
                      </ListItem>
                    )}
                  {item.sectionDivider && (
                    <SectionDivider sx={{ marginLeft: '0', marginRight: '0', marginBottom: '8px' }} />
                  )}
                </Box>
              ))}
            </List>
            {selectedTeamOwners.find((owner) => owner.displayName === user.fullName) && (
              <Button
                sx={{
                  color: '#BE266A',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  textTransform: 'none',
                  textDecoration: 'underline',
                  padding: '0',
                }}
                onClick={confirmDeleteTeam}
              >
                {t('rightPanel.team.editTeam.deleteTeam')}
              </Button>
            )}
          </>
        )}

        {isDeleteModalOpen && (
          <DeleteModal
            openDeleteModal={isDeleteModalOpen}
            closeDeleteModal={closeDeleteModal}
            modalAPi={confirmDelete}
            dialogTitle={
              teamSharedUserToBeDeleted
                ? t('rightPanel.team.editTeam.removeTeamSharedUser')
                : t('rightPanel.team.editTeam.deleteTeam')
            }
            textContent={
              teamSharedUserToBeDeleted
                ? `${t('rightPanel.confirmRemove')} ${teamSharedUserToBeDeleted.displayName}?`
                : `${t('rightPanel.confirmDelete')} ${team.name}?`
            }
            confirmButtonText={t('rightPanel.removeButton')}
          />
        )}
      </Box>

      {showShareTeamDialog && (
        <Box>
          <ShareTeam
            setShowShareTeamDialog={setShowShareTeamDialog}
            team={team}
            selectedTeamSharedUsers={selectedTeamSharedUsers}
            selectedTeamOwners={selectedTeamOwners}
            isNavigatedThroughManageShare={true}
          />
        </Box>
      )}
    </>
  );
};

export default EditTeam;
